import { type RefObject, useCallback, useEffect } from 'react';

import { type AiBuilderMessage } from '../store';

/**
 * 메시지 추가/콘텐츠 리사이즈 시 채팅 스크롤을 바닥에 붙인다.
 * 렌더 직후 한 번 + 다음 프레임 한 번 스크롤해 레이아웃 지연을 흡수한다.
 */
export const useAiBuilderChatAutoScroll = ({
  contentRef,
  messages,
  scrollRef,
}: {
  contentRef: RefObject<HTMLDivElement | null>;
  messages: AiBuilderMessage[];
  scrollRef: RefObject<HTMLDivElement | null>;
}) => {
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) return;

      scrollAiBuilderChatPanelToBottom(scrollElement, behavior);
    },
    [scrollRef],
  );

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    scrollAiBuilderChatPanelToBottom(scrollElement);

    return requestAiBuilderChatScrollFrame(() => {
      scrollAiBuilderChatPanelToBottom(scrollElement);
    });
  }, [messages, scrollRef]);

  useEffect(() => {
    const contentElement = contentRef.current;
    const scrollElement = scrollRef.current;

    if (
      !contentElement ||
      !scrollElement ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }

    let cancelScrollFrame: (() => void) | undefined;
    const resizeObserver = new ResizeObserver(() => {
      cancelScrollFrame?.();
      cancelScrollFrame = requestAiBuilderChatScrollFrame(() => {
        scrollToBottom('auto');
      });
    });

    resizeObserver.observe(contentElement);
    resizeObserver.observe(scrollElement);

    return () => {
      cancelScrollFrame?.();
      resizeObserver.disconnect();
    };
  }, [contentRef, scrollRef, scrollToBottom]);
};

const scrollAiBuilderChatPanelToBottom = (
  scrollElement: HTMLDivElement,
  behavior: ScrollBehavior = 'smooth',
) => {
  if (typeof scrollElement.scrollTo === 'function') {
    scrollElement.scrollTo({
      top: scrollElement.scrollHeight,
      behavior,
    });
    return;
  }

  scrollElement.scrollTop = scrollElement.scrollHeight;
};

const requestAiBuilderChatScrollFrame = (callback: FrameRequestCallback) => {
  if (typeof window.requestAnimationFrame !== 'function') {
    callback(0);
    return undefined;
  }

  const frameId = window.requestAnimationFrame(callback);

  return () => window.cancelAnimationFrame(frameId);
};
